import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';

import { messagingService } from '../../../../services/messaging.service';
import { theme } from '../../../../theme';

const ClientChatThread = ({
  conversation,
  onConversationChange,
  onBack,
}) => {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const closed = conversation?.status === 'closed';

  useEffect(() => {
    setDraft('');
    setError('');
  }, [conversation?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ block: 'end' });
  }, [conversation?.messages]);

  const send = async () => {
    const content = draft.trim();
    if (!content || busy || closed || !conversation?.id) return;

    setBusy(true);
    setError('');
    setDraft('');
    try {
      const response = await messagingService.sendClientConversationMessage(
        conversation.id,
        content,
      );
      onConversationChange?.(response.data);
    } catch (_requestError) {
      setDraft(content);
      setError('Impossible d’envoyer le message. Réessayez.');
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      send();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      {onBack && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 0.75,
            borderBottom: `1px solid ${theme.colors.primary[100]}`,
          }}
        >
          <IconButton
            aria-label="Retour aux discussions"
            onClick={onBack}
            size="small"
            sx={{ color: theme.colors.primary[950] }}
          >
            <ArrowBackRoundedIcon />
          </IconButton>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Toutes les discussions
          </Typography>
        </Box>
      )}

      <Box
        aria-live="polite"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 2,
          py: 2.25,
          background: `linear-gradient(180deg, ${theme.colors.secondary[50]} 0%, ${theme.colors.primary[50]} 100%)`,
        }}
      >
        {(conversation?.messages || []).map((message) => {
          const own = message.sender_type === 'client';
          return (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: own ? 'flex-end' : 'flex-start',
                mb: 1.25,
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  maxWidth: '78%',
                  px: 1.75,
                  py: 1.15,
                  backgroundColor: own
                    ? theme.colors.primary[800]
                    : theme.colors.secondary[100],
                  color: own ? 'white' : theme.colors.grey[900],
                  border: own
                    ? 'none'
                    : `1px solid ${theme.colors.secondary[200]}`,
                  borderRadius: own
                    ? '16px 16px 4px 16px'
                    : '16px 16px 16px 4px',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
                >
                  {message.content}
                </Typography>
              </Paper>
            </Box>
          );
        })}
        <Box ref={messagesEndRef} />
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          p: 1.5,
          borderTop: `1px solid ${theme.colors.primary[100]}`,
          backgroundColor: theme.colors.secondary[50],
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        {closed && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 0.75, color: theme.colors.grey[600] }}
          >
            Cette conversation est fermée. Son historique reste disponible.
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            size="small"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              closed
                ? 'Conversation fermée'
                : 'Écrivez votre message…'
            }
            disabled={closed || busy}
            inputProps={{ 'aria-label': 'Votre message' }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '18px',
                backgroundColor: 'white',
                '&.Mui-focused fieldset': {
                  borderColor: theme.colors.primary[700],
                },
              },
            }}
          />
          <IconButton
            aria-label="Envoyer"
            onClick={send}
            disabled={!draft.trim() || busy || closed}
            sx={{
              width: 42,
              height: 42,
              flexShrink: 0,
              backgroundColor: theme.colors.primary[800],
              color: 'white',
              '&:hover': {
                backgroundColor: theme.colors.primary[900],
              },
              '&.Mui-disabled': {
                backgroundColor: theme.colors.grey[200],
                color: theme.colors.grey[500],
              },
            }}
          >
            {busy ? (
              <CircularProgress size={19} color="inherit" />
            ) : (
              <SendRoundedIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default ClientChatThread;
